import React, { useEffect, useState, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import { Link, useParams } from "react-router-dom";
import { LuRefreshCcw } from "react-icons/lu";
import { FaSpinner } from "react-icons/fa";
import {
  showToast,
  showAlert,
  formatDateWithDayjs,
  formatCurrencyWithLocale,
  downloadFileFromResponse
} from "../../../utils/utils";
import {
  InvoiceDetails,
  fileDownloadMessages,
  alertActions,
  dashboard,
  tooltips,
  commonText
} from "../../../locales/en";
import privateAxios from "../../../utils/axiosPrivate";
import {
  showToastType,
  CURRENCY_FORMAT,
  INVOICE_STATUS_VOIDED
} from "../../../utils/constants/appConstants";
import DebtorPaymentDetailsModal from "../../../components/modals/DebtorPaymentDetailsModal";
import styles from "./../../../assets/scss/custom.module.scss";
import "./details.scss";
import { Tooltip as ReactTooltip } from "react-tooltip";
import {
  getRefreshInvoiceStatusURL,
  loanInvDownloadURL,
  loanInvUploadDocURL
} from "../../../utils/constants/apiEndPoints";
import { CLIENT_DETAILS } from "../../../route/paths";
import { updateDebtorName } from "../../../redux/invoiceDetailsSlice";
import { useDispatch } from "react-redux";

const Details = (props) => {
  const invoiceIdsListRef = useRef(null);
  const dispatch = useDispatch();
  const { invData, fetchInvoiceData, legalName } = props;
  const { id } = useParams();
  const inputRef = useRef(null);

  const [editable, setEditable] = useState(false);
  const [InvDetails, setInvDetails] = useState({});
  const [debtorName, setDebtorName] = useState(null);
  const [filePath, setFilePath] = useState("");
  const [isID, setIsID] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadSpin, setLoadSpin] = useState(false);
  const [isInvoiceUploaded, setIsInvoiceUploaded] = useState(false);
  const [uploadedFile, setUploadedFile] = useState();
  const [messageShow, setMessageShow] = useState(false);
  const fileInputRef = useRef(null);
  const userAccountId = invData?.userAccountId;
  const fundedAt = invData?.fundedAt;

  const handleACCstatusRefresh = async () => {
    try {
      setLoadSpin(true);
      const response = await privateAxios.get(getRefreshInvoiceStatusURL, {
        params: { userAccountId, quoteId: id }
      });
      showToast(
        response?.data?.message ||
          InvoiceDetails?.detailsSection?.error?.success,
        showToastType?.success
      );
    } catch (error) {
      showToast(
        error?.response?.data?.message ||
          InvoiceDetails?.detailsSection?.error?.faield,
        showToastType?.error
      );
    } finally {
      setLoadSpin(false);
    }
  };

  const handleInvoiceDownload = useCallback(
    async (event) => {
      event.preventDefault();
      try {
        setLoading(true);
        const response = await privateAxios.get(loanInvDownloadURL, {
          params: { invoiceId: isID },
          responseType: "blob"
        });
        downloadFileFromResponse(response);
      } catch (error) {
        showToast(
          error?.response?.data?.message ||
            fileDownloadMessages?.errors?.downloadFailed,
          showToastType?.error
        );
      } finally {
        setLoading(false);
      }
    },
    [isID, filePath]
  );

  const triggerFileUpload = () => fileInputRef.current?.click();

  const fileUpload = (e) => {
    setUploadedFile(e.target.files[0]);
    setMessageShow(!!e.target.files[0]);
  };

  const handleInvoiceUpload = async () => {
    const invoiceFile = uploadedFile;
    const invoiceId = isID;

    if (invoiceFile && invoiceId) {
      try {
        setLoading(true);
        const formData = new FormData();
        formData.append("file", invoiceFile);

        const response = await privateAxios.put(
          loanInvUploadDocURL(invoiceId),
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
        setIsInvoiceUploaded(true);
        showToast(response.data.message, showToastType?.success);
        setMessageShow(false);
        if (typeof fetchInvoiceData === "function") {
          await fetchInvoiceData();
        }
      } catch (error) {
        showToast(
          error.response?.data?.message ||
            fileDownloadMessages?.errors?.uploadFaield,
          showToastType?.error
        );
      } finally {
        setLoading(false);
      }
    }
  };

  const handleInputChange = (event) => setDebtorName(event.target.value);

  const handleEditOptionDebtor = async () => {
    const result = await showAlert(
      InvoiceDetails?.detailsSection?.alert?.title,
      InvoiceDetails?.detailsSection?.alert?.subTitle,
      alertActions?.warning,
      alertActions?.Confirm,
      alertActions?.Cancel
    );
    setEditable(result.isConfirmed);
  };

  const handleUpdateDebtor = async () => {
    try {
      await dispatch(
        updateDebtorName({
          quoteId: Number(id),
          debtorName
        })
      ).unwrap();
      setEditable(false);
    } catch (error) {
      showToast(
        error.response?.data?.message || showToastType?.errorText,
        showToastType?.error
      );
    }
  };

  const [payHistoryModal, setPayHistoryModal] = useState(false);

  const handleDebtorPaymentModal = () => {
    setPayHistoryModal(!payHistoryModal);
  };

  const handleDebtorClickData = (row) => {
    setPayHistoryModal(true);
    invoiceIdsListRef.current = row;
  };

  useEffect(() => {
    if (invData) {
      setInvDetails(invData?.invoice);
      setDebtorName(invData?.invoice?.debatorName);
      setFilePath(invData?.invoice?.invoiceFilePath);
      setIsID(invData?.invoice?.id);
    }
  }, [invData]);

  const commonModalProps = {
    debtorName,
    invoiceIdsListRef,
    invoiceDtailsPage: true,
    invData
  };

  const tooltipsData = [
    { id: "tooltip-editDebtor", content: tooltips?.editDebtor },
    { id: "tooltip-ACCstatusRefresh", content: tooltips?.accStatusRefresh },
    { id: "tooltip-debtorPaidHistory", content: tooltips?.debtorPaidHistory }
  ];

  const renderSentInvoiceAction = () => {
    const { autoDownload, manualCheck, upload, download } =
      InvoiceDetails?.detailsSection?.btn ?? {};

    if (messageShow) {
      return (
        <Link onClick={handleInvoiceUpload}>
          {loading ? `${upload}` : `${tooltips?.submitInvoice}`}
        </Link>
      );
    }

    if (InvDetails?.externalInvoiceStatus === autoDownload) {
      return `${commonText?.no}`;
    }

    if (
      (InvDetails?.invoiceFilePath || isInvoiceUploaded) &&
      InvDetails?.externalInvoiceStatus === manualCheck
    ) {
      return (
        <Link onClick={(e) => handleInvoiceDownload(e)}>
          {loading ? `${download}` : `${tooltips?.invoiceCopy}`}
        </Link>
      );
    }

    if (
      InvDetails?.externalInvoiceStatus === manualCheck &&
      !InvDetails?.invoiceFilePath
    ) {
      return (
        <Link onClick={triggerFileUpload}>{tooltips?.uploadInvoiceCopy}</Link>
      );
    }

    return `${InvoiceDetails?.detailsSection?.showText?.text1}`;
  };

  return (
    <>
      <div>
        <div className={styles.titleArea}>
          <h5>{InvoiceDetails?.detailsSection?.title}</h5>
        </div>
        <div className="row">
          <div className="col-12 col-md-6 col-lg-8 mb-3">
            <div className="blog-input-wrap">
              <div className={`${styles.labelTitle} border-bottom`}>
                <h5>{InvoiceDetails?.detailsSection?.customer}</h5>
              </div>
              <div
                className={`${styles.linkwrap} d-flex justify-content-between`}
              >
                <Link to={CLIENT_DETAILS(InvDetails?.userAccountId)}>
                  {legalName}
                </Link>
              </div>
            </div>
          </div>
          <div className="col-12 col-md-6 col-lg-4 mb-3">
            <div className="blog-input-wrap">
              <div className={`${styles.labelTitle} border-bottom`}>
                <h5>{InvoiceDetails?.detailsSection?.invNo}</h5>
              </div>
              <p>{InvDetails?.invoiceNumber}</p>
            </div>
          </div>
          <div className="col-12 col-md-6 col-lg-8 mb-3">
            <div className="blog-input-wrap">
              <div className={`${styles.labelTitle} border-bottom`}>
                <h5>{InvoiceDetails?.detailsSection?.debtor}</h5>
              </div>
              <div
                className={`${styles.linkwrap} d-flex justify-content-between editDebtor`}
              >
                {editable === true ? (
                  <input
                    type="text"
                    name="debtorName"
                    className={styles.textfield}
                    value={debtorName}
                    ref={inputRef}
                    onChange={handleInputChange}
                    onBlur={handleUpdateDebtor}
                  />
                ) : (
                  <p>{debtorName}</p>
                )}

                <ul
                  className={`${styles.btngroupwrap} d-flex justify-content-end align-items-center`}
                >
                  <li>
                    <span
                      className={`${styles.btnPrimary} edit-icon added-icon`}
                    >
                      <input
                        type="button"
                        onClick={handleEditOptionDebtor}
                        data-tooltip-id="tooltip-editDebtor"
                      />
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
          <div className="col-12 col-md-6 col-lg-4 mb-3">
            <div className="blog-input-wrap">
              <div className={`${styles.labelTitle} border-bottom`}>
                <h5>{InvoiceDetails?.detailsSection?.debtorLastPaid}</h5>
              </div>
              <ul
                className={`${styles.btngroupwrap} d-flex justify-content-end align-items-center`}
              >
                <li>
                  <span
                    className={`${styles.btnPrimary} clock-icon added-icon`}
                    onClick={handleDebtorClickData}
                    data-tooltip-id="tooltip-debtorPaidHistory"
                  >
                    <input type="button" />
                  </span>
                </li>
              </ul>
            </div>
          </div>
          <div className="col-12 col-md-6 col-lg-4 mb-3">
            <div className="blog-input-wrap">
              <div className={`${styles.labelTitle} border-bottom`}>
                <h5>{InvoiceDetails?.detailsSection?.invAmount}</h5>
              </div>
              <p>
                {CURRENCY_FORMAT}
                {formatCurrencyWithLocale(invData?.invoice?.totalAmount || 0)} (
                {CURRENCY_FORMAT}
                {formatCurrencyWithLocale(
                  invData?.invoice?.outstandingAmount || 0
                )}
                )
              </p>
            </div>
          </div>
          <div className="col-12 col-md-6 col-lg-4 mb-3">
            <div className="blog-input-wrap">
              <div className={`${styles.labelTitle} border-bottom`}>
                <h5>{InvoiceDetails?.detailsSection?.accStatus}</h5>
              </div>
              <div
                className={`${styles.linkwrap} d-flex justify-content-between`}
              >
                <p
                  className={`${
                    invData?.invoice?.externalInvoiceStatus ===
                    INVOICE_STATUS_VOIDED
                      ? "text-danger"
                      : ""
                  }`}
                >
                  {invData?.invoice?.externalInvoiceStatus}
                </p>

                <ul
                  className={`${styles.btngroupwrap} d-flex justify-content-end align-items-center`}
                >
                  <li>
                    <button
                      type="button"
                      className={`${styles.btnPrimary} edit-icon added-icon`}
                      onClick={handleACCstatusRefresh}
                      data-tooltip-id="tooltip-ACCstatusRefresh"
                    >
                      {loadSpin ? (
                        <span className="loadSpin">
                          <FaSpinner />
                        </span>
                      ) : (
                        <LuRefreshCcw />
                      )}
                    </button>
                  </li>
                </ul>
              </div>
            </div>
          </div>
          <div className="col-12 col-md-6 col-lg-4 mb-3">
            <div className="blog-input-wrap">
              <div className={`${styles.labelTitle} border-bottom`}>
                <h5>{InvoiceDetails?.detailsSection?.sent}</h5>
              </div>
              {renderSentInvoiceAction()}
              <input
                type="file"
                name="file"
                accept="application/pdf,image/*"
                onChange={(e) => fileUpload(e)}
                ref={fileInputRef}
                style={{ display: "none" }}
              />
            </div>
          </div>
          <div className="col-12 col-md-6 col-lg-4 mb-3">
            <div className="blog-input-wrap">
              <div className={`${styles.labelTitle} border-bottom`}>
                <h5>{InvoiceDetails?.detailsSection?.dateCreated}</h5>
              </div>
              {invData?.invoice?.invoiceDate && (
                <p>
                  {formatDateWithDayjs(
                    invData?.invoice?.invoiceDate,
                    "DD-MMM-YYYY"
                  )}
                </p>
              )}
            </div>
          </div>
          <div className="col-12 col-md-6 col-lg-4 mb-3">
            <div className="blog-input-wrap">
              <div className={`${styles.labelTitle} border-bottom`}>
                <h5>{InvoiceDetails?.detailsSection?.dateSubmitted}</h5>
              </div>
              {invData?.submittedAt && (
                <p>{formatDateWithDayjs(invData.submittedAt, "DD-MMM-YYYY")}</p>
              )}
            </div>
          </div>
          <div className="col-12 col-md-6 col-lg-4 mb-3">
            <div className="blog-input-wrap">
              <div className={`${styles.labelTitle} border-bottom`}>
                <h5>{dashboard?.invoiceDatatable.dateFunded}</h5>
              </div>
              {fundedAt && (
                <p>{formatDateWithDayjs(fundedAt, "DD-MMM-YYYY")}</p>
              )}
            </div>
          </div>
        </div>
      </div>

      <DebtorPaymentDetailsModal
        isOpen={payHistoryModal}
        toggle={handleDebtorPaymentModal}
        {...commonModalProps}
      />

      {tooltipsData.map(({ id, content }) => (
        <ReactTooltip
          key={id}
          className="tooltipcustom"
          id={id}
          place="top"
          content={content}
        />
      ))}
    </>
  );
};
Details.propTypes = {
  invData: PropTypes.object,
  fetchInvoiceData: PropTypes.func,
  legalName: PropTypes.string
};

export default Details;
